import React, { useState } from 'react';
import { FaBan, FaCheck, FaPen, FaPlus, FaTimes, FaUndo, FaUnlock } from 'react-icons/fa';
import Boton from './Boton';
import EditarFila from './EditarFila';
import { conMiles, formatearFechaHora } from './formatos';
import '../../styles/components/PropuestaCarga.css';

const vacio = (valor) => valor === null || valor === undefined || String(valor).trim() === '';

// Una columna se muestra si alguna fila trae texto o un número distinto de cero
const tieneDato = (columna, valor) => (columna.tipo === 'C' ? !vacio(valor) : !vacio(valor) && Number(valor) !== 0);

function formatear(columna, fila) {
    const valor = fila[columna.campo];
    if (vacio(valor)) return '';
    if (columna.tipo === 'C') return valor;
    if (columna.tipo === 'P') {
        return Number(valor).toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    const numero = conMiles(valor);
    return columna.campo === 'operacion' && fila.operacion_nombre ? `${numero} · ${fila.operacion_nombre}` : numero;
}

// Para agregar se abre el mismo formulario con una fila vacía
const FILA_NUEVA = { linea_origen: null, avisos: [] };

const enOtraMoneda = (fila) => Boolean(fila.moneda) && fila.moneda !== 'CLP';

// «línea 5» (archivo), «fila agregada» (contador) o «fila 3 (pág. 2)» (PDF)
function nombreDeFila(fila) {
    if (fila.linea_origen) return `línea ${fila.linea_origen}`;
    if (fila.origen === 'manual') return 'fila agregada';
    return `fila ${fila.orden}${fila.pagina_origen ? ` (pág. ${fila.pagina_origen})` : ''}`;
}

// «USD · $962,60», o «USD · sin tasa» mientras EFE no la registre
const monedaDeFila = (fila) => `${fila.moneda} · ${fila.tasa_valor
    ? `$${Number(fila.tasa_valor).toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`
    : 'sin tasa'}`;

/**
 * Propuesta de una carga del módulo Inversiones exterior: las filas como las leyó
 * TAXI, con sus errores y avisos, y los totales de las filas incluidas para
 * compararlos con el origen. Mientras la carga está por revisar, el contador puede
 * editar, agregar, excluir o incluir filas (todo opcional) y validarla; una carga
 * validada queda en solo lectura hasta reabrirla. Las columnas, las listas de códigos
 * y los avisos vienen del servidor (formato1929.js).
 *
 * @param {Object} props
 * @param {Object} props.carga - Carga con columnas, códigos, relaciones, filas y totales
 * @param {Object[]|null} props.paises - Países para la columna País
 * @param {Object} props.acciones - guardarFila(idFila, valores) y agregarFila(valores) devuelven
 *   true si guardaron; cambiarInclusion(fila), validar() y reabrir()
 * @param {Function} props.onCerrar - Cierra la propuesta
 */
const PropuestaCarga = ({ carga, paises, acciones, onCerrar }) => {
    const [formulario, setFormulario] = useState(null);
    const { columnas, filas, totales } = carga;
    const porRevisar = carga.estado === 'por_revisar';
    const visibles = columnas.filter((columna) => filas.some((fila) => tieneDato(columna, fila[columna.campo])));
    const ocultas = columnas.filter((columna) => !visibles.includes(columna));
    const excluidas = filas.filter((fila) => !fila.incluida).length;
    const conNivel = (nivel) => filas.filter((fila) => fila.incluida && fila.avisos.some((aviso) => aviso.nivel === nivel)).length;
    const conError = conNivel('error');
    const conAviso = conNivel('aviso');
    // La columna Moneda aparece solo si alguna fila viene en otra moneda (PDF, …)
    const hayOtraMoneda = filas.some(enOtraMoneda);

    const abrirEdicion = (fila) => setFormulario({ fila, titulo: `Editar ${nombreDeFila(fila)}` });

    const guardar = async (valores) => {
        const guardo = formulario.fila === FILA_NUEVA
            ? await acciones.agregarFila(valores)
            : await acciones.guardarFila(formulario.fila.id, valores);
        if (guardo) setFormulario(null);
    };

    return (
        <div className="invext-pc">
            <div className="invext-pc-cabecera">
                <div>
                    <h2>Propuesta · {carga.nombre_archivo}</h2>
                    {(carga.institucion || carga.url_archivo) && (
                        <p className="invext-pc-origen">
                            {carga.institucion}
                            {carga.institucion && carga.url_archivo && ' · '}
                            {carga.url_archivo && <a href={carga.url_archivo} target="_blank" rel="noopener noreferrer">Ver el documento</a>}
                        </p>
                    )}
                    <p>
                        {totales.casos} filas incluidas{excluidas > 0 && `, ${excluidas} excluidas`}, montos en pesos.
                        {hayOtraMoneda && ' Las filas en otra moneda se pasaron a pesos con la tasa al 31-12.'}
                        {conError > 0 && ` ${conError} con errores, que impiden validar.`}
                        {conAviso > 0 && ` ${conAviso} con avisos.`}
                        {porRevisar && ' Puedes editar, agregar o excluir filas; es opcional.'}
                    </p>
                    {carga.estado === 'validada' && (
                        <p className="invext-pc-validada">
                            Validada el {formatearFechaHora(carga.validado_en)}: va a la DJ 1929. Para cambiarla, reábrela.
                        </p>
                    )}
                </div>
                <div className="invext-botones invext-pc-acciones">
                    {porRevisar && (
                        <>
                            <Boton icono={<FaPlus />} onClick={() => setFormulario({ fila: FILA_NUEVA, titulo: 'Agregar fila' })}>
                                Agregar fila
                            </Boton>
                            <Boton variante="primario" icono={<FaCheck />} onClick={acciones.validar}>
                                Validar carga
                            </Boton>
                        </>
                    )}
                    {carga.estado === 'validada' && (
                        <Boton icono={<FaUnlock />} onClick={acciones.reabrir}>Reabrir</Boton>
                    )}
                    <Boton variante="discreto" icono={<FaTimes />} onClick={onCerrar}>Cerrar</Boton>
                </div>
            </div>

            <div className="invext-pc-tabla-wrap">
                <table className="invext-pc-tabla">
                    <thead>
                        <tr>
                            {porRevisar && <th aria-label="Acciones" />}
                            <th className="num">Línea</th>
                            {hayOtraMoneda && <th>Moneda</th>}
                            {visibles.map((columna) => (
                                <th key={columna.campo} className={columna.tipo === 'C' ? '' : 'num'}>{columna.titulo}</th>
                            ))}
                            <th>Avisos</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filas.map((fila) => {
                            const nivelPorCampo = Object.fromEntries(fila.avisos.map((aviso) => [aviso.campo, aviso.nivel]));
                            return (
                                <tr key={fila.id} className={fila.incluida ? '' : 'excluida'}>
                                    {porRevisar && (
                                        <td>
                                            <div className="invext-botones en-fila">
                                                <Boton
                                                    chico
                                                    icono={<FaPen />}
                                                    onClick={() => abrirEdicion(fila)}
                                                    disabled={!fila.incluida}
                                                    title={fila.incluida ? undefined : 'Inclúyela para editarla'}
                                                >
                                                    Editar
                                                </Boton>
                                                <Boton
                                                    chico
                                                    icono={fila.incluida ? <FaBan /> : <FaUndo />}
                                                    onClick={() => acciones.cambiarInclusion(fila)}
                                                >
                                                    {fila.incluida ? 'Excluir' : 'Incluir'}
                                                </Boton>
                                            </div>
                                        </td>
                                    )}
                                    <td className="num">
                                        {fila.linea_origen ?? (fila.pagina_origen ? `pág. ${fila.pagina_origen}` : '—')}
                                        {fila.origen === 'manual' && <span className="invext-pc-marca agregada">Agregada</span>}
                                        {fila.editada && <span className="invext-pc-marca editada">Editada</span>}
                                        {!fila.incluida && <span className="invext-pc-marca excluida">Excluida</span>}
                                    </td>
                                    {hayOtraMoneda && (
                                        <td className={`moneda ${enOtraMoneda(fila) && !fila.tasa_valor ? 'con-error' : ''}`}>
                                            {enOtraMoneda(fila) ? monedaDeFila(fila) : 'CLP'}
                                        </td>
                                    )}
                                    {visibles.map((columna) => (
                                        <td
                                            key={columna.campo}
                                            className={[
                                                'valor',
                                                columna.tipo === 'C' ? 'texto' : 'num',
                                                nivelPorCampo[columna.campo] ? `con-${nivelPorCampo[columna.campo]}` : ''
                                            ].join(' ')}
                                        >
                                            {formatear(columna, fila)}
                                        </td>
                                    ))}
                                    <td className="avisos">
                                        {fila.avisos.length > 0 && (
                                            <ul>
                                                {fila.avisos.map((aviso, indice) => (
                                                    <li key={indice} className={aviso.nivel}>{aviso.mensaje}</li>
                                                ))}
                                            </ul>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                    <tfoot>
                        <tr>
                            {porRevisar && <td />}
                            <td>Total</td>
                            {hayOtraMoneda && <td />}
                            {visibles.map((columna) => (
                                <td key={columna.campo} className="num">
                                    {totales[columna.campo] !== undefined ? conMiles(totales[columna.campo]) : ''}
                                </td>
                            ))}
                            <td />
                        </tr>
                    </tfoot>
                </table>
            </div>

            {ocultas.length > 0 && (
                <p className="invext-pc-nota">Vacías o en cero en todas las filas: {ocultas.map((columna) => columna.titulo).join(', ')}.</p>
            )}

            {formulario && (
                <EditarFila
                    titulo={formulario.titulo}
                    fila={formulario.fila}
                    columnas={columnas}
                    codigos={carga.codigos}
                    relaciones={carga.relaciones}
                    paises={paises}
                    onGuardar={guardar}
                    onCerrar={() => setFormulario(null)}
                />
            )}
        </div>
    );
};

export default PropuestaCarga;
